import React, { ReactNode, useMemo } from 'react'
import {
  inspectorHoveredNodeContext,
  inspectorContext,
  useInspectorOverlayEvents,
  InspectorContext,
  useGetInspectorData,
  useAugmentLayersTreeWithInspectorData,
  useHasAnyLayerWithExportableAssets,
} from '../../hooks/inspector'
import { useGetActiveSidebarTab } from 'modules/shares/components/ShareSidebarTabContext'

type ArtboardDetailInspectorProviderProps = {
  children: ReactNode
  artboardId: string | undefined
  artboardIsSymbolMaster: boolean | undefined
}

export function ArtboardDetailInspectorProvider({
  artboardId,
  children,
  artboardIsSymbolMaster,
}: ArtboardDetailInspectorProviderProps) {
  const activeSidebarTab = useGetActiveSidebarTab()
  const isInspectorActive = activeSidebarTab === 'Inspect'

  const inspectorDataQueryResult = useGetInspectorData(
    isInspectorActive ? artboardId : undefined
  )

  const hasAnyLayerWithExportableAssets = useHasAnyLayerWithExportableAssets(
    inspectorDataQueryResult.inspectorData
  )

  /**
   * useMemo to avoid rebuilding the tree at each render
   * inside useAugmentLayersTreeWithInspectorData
   */
  const artboardGraphQLData = useMemo(() => {
    if (typeof artboardIsSymbolMaster === 'undefined') {
      return undefined
    }

    return { artboardIsSymbolMaster }
  }, [artboardIsSymbolMaster])

  const inspectorLayersTreeRootNode = useAugmentLayersTreeWithInspectorData(
    inspectorDataQueryResult.inspectorData,
    artboardGraphQLData
  )

  const {
    selectedNode,
    hoveredNode,
    setSelectedNode,
    clickHandler,
    mouseMoveHandler,
  } = useInspectorOverlayEvents(
    inspectorLayersTreeRootNode,
    // Handle web renderer events only when we need to
    isInspectorActive && inspectorDataQueryResult.inspectorDataIsReady
  )

  const inspectorContextValue = useMemo((): InspectorContext => {
    return {
      selectedNode: selectedNode,
      isInspectorActive: isInspectorActive,
      inspectorDataIsReady: inspectorDataQueryResult.inspectorDataIsReady,
      layersTreeRootNode: inspectorLayersTreeRootNode,
      hasInspectorDataError:
        Boolean(inspectorDataQueryResult.inspectorApolloError) ||
        inspectorDataQueryResult.hasDataParsingError,
      setSelectedLayerNode: setSelectedNode,
      isInspectorReady: Boolean(inspectorLayersTreeRootNode),
      hasAnyLayerWithExportableAssets,
      clickHandler,
      mouseMoveHandler,
    }
  }, [
    clickHandler,
    hasAnyLayerWithExportableAssets,
    inspectorDataQueryResult.hasDataParsingError,
    inspectorDataQueryResult.inspectorApolloError,
    inspectorDataQueryResult.inspectorDataIsReady,
    inspectorLayersTreeRootNode,
    isInspectorActive,
    mouseMoveHandler,
    selectedNode,
    setSelectedNode,
  ])

  return (
    <inspectorContext.Provider value={inspectorContextValue}>
      <inspectorHoveredNodeContext.Provider value={hoveredNode}>
        {children}
      </inspectorHoveredNodeContext.Provider>
    </inspectorContext.Provider>
  )
}
